import {AxiosError} from 'axios';
import {FastAverageColor} from 'fast-average-color';
import {Form, Formik} from 'formik';
import {ChangeEvent, useCallback, useState, useEffect} from 'react';
import {useNavigate, useParams} from 'react-router-dom';

import CustomHeader from '../../../../../components/mazraeKit/headers/homeHeader';
import useGetStoryLimit from '../../../../../services/hookService/getStoryLimit/useGetStoryLimit';
import usePostStory from '../../../../../services/hookService/postStory/usePostStory';
import {storyValidateSchema} from '../../contants';
import ImageRequirementsText from './ImageRequirementsText';
import ImageUploadSection from './ImageUploadSection';
import LinkDescriptionSection from './LinkDescriptionSection';
import LinkInputSection from './LinkInputSection';
import Modals from './Modals';
import StoryCreditSection from './StoryCreditSection';
import StoryDescription from './StoryDescription';
import SubmitButton from './SubmitButton';

interface FormValues {
  link: string;
  link_description: string;
  preview_color: string;
}

const SetStory = () => {
  const navigate = useNavigate();
  const {guid} = useParams();
  const [isShowAddStoryBs, setIsShowAddStoryBs] = useState(false);
  const [showCropModal, setShowCropModal] = useState(false);
  const [currentImage, setCurrentImage] = useState<File | null>(null);
  const [croppedImageFile, setCroppedImageFile] = useState<File | null>(null);
  const [isSuccessModal, setIsSuccessModal] = useState(false);

  const {data: storyLimitData} = useGetStoryLimit();
  const {mutate: postStory, isLoading: isPostingStory} = usePostStory();

  // Image handling
  const handleCropComplete = useCallback((croppedImage: File) => {
    setCroppedImageFile(croppedImage);
    setShowCropModal(false);
  }, []);

  const handleImageChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setCurrentImage(file);
    setShowCropModal(true);
    e.target.value = '';
  }, []);

  const removeCroppedImage = useCallback(() => {
    setCroppedImageFile(null);
  }, []);

  // Form submission
  const handleSubmit = useCallback(
    (values: FormValues) => {
      if (!croppedImageFile || !guid) return;

      const payload = {
        ...values,
        image: croppedImageFile,
        store_guid: guid,
      };

      postStory(payload, {
        onSuccess: () => setIsSuccessModal(true),
        onError: (error: AxiosError<IAxiosErr>) => {
          if (error.response?.status === 403) {
            setIsShowAddStoryBs(true);
          }
        },
      });
    },
    [croppedImageFile, guid, postStory],
  );

  // Navigation handlers
  const navigateToStoreInfo = useCallback(() => {
    navigate('/my-store-info');
  }, [navigate]);

  const navigateToGuide = useCallback(() => {
    navigate('/guide-story', {state: {storyGuid: guid}});
  }, [navigate, guid]);

  // Initial form values
  const initialValues: FormValues = {
    link: `/shop-card-detail/${guid}`,
    link_description: '',
    preview_color: '',
  };

  return (
    <div>
      <CustomHeader
        title="ثبت استوری"
        preIcon="icon-nameArrow---Right"
        onClick={navigateToStoreInfo}
        isActiveRightIcon
        postComponent={() => (
          <i
            className="icon-namereport text-[25px] text-farm-shop-500 cursor-pointer"
            onClick={navigateToGuide}
          />
        )}
      />

      <Formik
        initialValues={initialValues}
        validationSchema={storyValidateSchema}
        onSubmit={handleSubmit}
      >
        {({setFieldValue, values}) => {
          // Extract color only when croppedImageFile changes
          useEffect(() => {
            if (!croppedImageFile) return;

            const img = new Image();
            img.src = URL.createObjectURL(croppedImageFile);
            img.onload = async () => {
              try {
                const fac = new FastAverageColor();
                const color = await fac.getColorAsync(img);
                setFieldValue('preview_color', color.hexa);
              } catch (error) {
                console.error('Failed to extract image color:', error);
              }
            };

            // Cleanup
            return () => {
              URL.revokeObjectURL(img.src);
            };
          }, [croppedImageFile, setFieldValue]);

          return (
            <Form className="p-3">
              <StoryDescription />

              <ImageUploadSection
                croppedImageFile={croppedImageFile}
                onImageRemove={removeCroppedImage}
                onImageChange={handleImageChange}
              />

              <ImageRequirementsText />

              <LinkInputSection
                value={values.link}
                onChange={value => setFieldValue('link', value)}
              />

              <LinkDescriptionSection
                value={values.link_description}
                onChange={value => setFieldValue('link_description', value)}
              />

              <StoryCreditSection remainingStories={storyLimitData?.remained} />

              <SubmitButton isLoading={isPostingStory} />
            </Form>
          );
        }}
      </Formik>

      <Modals
        showCropModal={showCropModal}
        currentImage={currentImage}
        onCropComplete={handleCropComplete}
        onCropClose={() => setShowCropModal(false)}
        isSuccessModal={isSuccessModal}
        onSuccessModalClose={() => setIsSuccessModal(false)}
        isAddStoryBsOpen={isShowAddStoryBs}
        onAddStoryBsClose={() => setIsShowAddStoryBs(false)}
      />
    </div>
  );
};

export default SetStory;
